import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Copy, Trash2, Loader2, Search, ShieldCheck, Sparkles, Users, Ticket, X, Check } from "lucide-react";
import AppShell from "@/components/AppShell";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const PLAN_OPTIONS = [
    { id: "trial", label: "Free Trial" },
    { id: "diy", label: "DIY ($29/mo)" },
    { id: "pro", label: "Restaurant Pro ($99/mo)" },
    { id: "agency", label: "Agency ($299/mo)" },
    { id: "comp", label: "Comp / Internal (free)" },
];

function StatTile({ label, value, icon: Icon }) {
    return (
        <div className="glass-card rounded-2xl p-5">
            <div className="flex items-center justify-between">
                <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">{label}</div>
                <Icon className="w-4 h-4 text-orange-400" />
            </div>
            <div className="font-heading text-3xl font-bold mt-2">{value}</div>
        </div>
    );
}

function GrantDialog({ user, onClose, onApplied }) {
    const [plan, setPlan] = useState("comp");
    const [credits, setCredits] = useState(500);
    const [saving, setSaving] = useState(false);
    if (!user) return null;
    const submit = async () => {
        setSaving(true);
        try {
            await api.post(`/admin/users/${user.id}/grant`, { plan, credits: Number(credits) });
            toast.success(`Granted ${credits} credits · ${plan} to ${user.email}`);
            onApplied();
            onClose();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Grant failed");
        } finally {
            setSaving(false);
        }
    };
    return (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm px-4" data-testid="grant-dialog">
            <motion.div
                initial={{ opacity: 0, y: 12, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                className="w-full max-w-md glass-card rounded-3xl p-7"
            >
                <div className="flex items-start justify-between mb-5">
                    <div>
                        <div className="text-xs font-mono uppercase tracking-widest text-orange-400 mb-1">Grant access</div>
                        <h3 className="font-heading text-xl font-semibold tracking-tight">{user.name}</h3>
                        <div className="text-sm text-zinc-500">{user.email}</div>
                    </div>
                    <button type="button" onClick={onClose} className="text-zinc-500 hover:text-white">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="space-y-4">
                    <div>
                        <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Plan</label>
                        <div className="mt-2 grid grid-cols-1 gap-2">
                            {PLAN_OPTIONS.map((p) => (
                                <button
                                    key={p.id}
                                    type="button"
                                    onClick={() => setPlan(p.id)}
                                    data-testid={`grant-plan-${p.id}`}
                                    className={`text-left px-3 py-2 rounded-lg border text-sm transition ${plan === p.id ? "border-orange-500/40 bg-orange-500/8 text-white" : "border-white/10 bg-white/3 text-zinc-400 hover:bg-white/8"}`}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Credits to add</label>
                        <input
                            type="number"
                            min="0"
                            value={credits}
                            onChange={(e) => setCredits(e.target.value)}
                            data-testid="grant-credits-input"
                            className="mt-2 w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-orange-500/60 font-mono"
                        />
                        <div className="mt-2 text-xs text-zinc-500">
                            Current: <span className="text-orange-400">{user.credits_remaining}</span> · After: <span className="text-orange-400">{user.credits_remaining + Number(credits || 0)}</span>
                        </div>
                    </div>
                </div>
                <div className="mt-6 flex gap-3 justify-end">
                    <button type="button" onClick={onClose} className="px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-sm text-zinc-300">
                        Cancel
                    </button>
                    <button type="button" onClick={submit} disabled={saving} data-testid="grant-apply-btn" className="btn-magic px-5 py-2 rounded-full text-white font-semibold inline-flex items-center gap-2 disabled:opacity-50">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        {saving ? "Applying..." : "Apply"}
                    </button>
                </div>
            </motion.div>
        </div>
    );
}

function UsersPanel() {
    const [users, setUsers] = useState([]);
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);
    const [granting, setGranting] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/users", { params: q ? { q } : {} });
            setUsers(data.users || []);
        } catch (_e) {
            toast.error("Failed to load users");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const id = setTimeout(load, 250);
        return () => clearTimeout(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q]);

    return (
        <div className="space-y-5">
            <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search by email, name or restaurant..."
                    data-testid="admin-user-search"
                    className="w-full bg-black/30 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-orange-500/60"
                />
            </div>

            <div className="glass-card rounded-2xl overflow-hidden">
                <div className="grid grid-cols-12 px-5 py-3 text-[10px] font-mono uppercase tracking-widest text-zinc-500 border-b border-white/5">
                    <div className="col-span-4">User</div>
                    <div className="col-span-3">Restaurant</div>
                    <div className="col-span-2">Plan</div>
                    <div className="col-span-1">Credits</div>
                    <div className="col-span-2 text-right">Actions</div>
                </div>
                {loading ? (
                    <div className="p-10 text-center text-zinc-500 text-sm">Loading...</div>
                ) : users.length === 0 ? (
                    <div className="p-10 text-center text-zinc-500 text-sm" data-testid="admin-users-empty">No users found.</div>
                ) : (
                    users.map((u) => (
                        <div key={u.id} className="grid grid-cols-12 px-5 py-3.5 items-center text-sm border-b border-white/5 last:border-b-0 hover:bg-white/3 transition" data-testid={`admin-user-row-${u.id}`}>
                            <div className="col-span-4 min-w-0">
                                <div className="truncate font-medium flex items-center gap-2">
                                    {u.name || "—"}
                                    {u.role === "admin" && (
                                        <span className="text-[9px] font-mono uppercase tracking-widest bg-rose-500/15 text-rose-300 px-1.5 py-0.5 rounded">admin</span>
                                    )}
                                </div>
                                <div className="truncate text-xs text-zinc-500">{u.email}</div>
                            </div>
                            <div className="col-span-3 truncate text-zinc-400 text-sm">{u.restaurant_name || "—"}</div>
                            <div className="col-span-2">
                                <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-zinc-300 capitalize">
                                    {u.plan}
                                </span>
                            </div>
                            <div className="col-span-1 font-mono text-orange-400 text-xs">{u.credits_remaining}</div>
                            <div className="col-span-2 flex justify-end">
                                <button
                                    type="button"
                                    onClick={() => setGranting(u)}
                                    data-testid={`admin-grant-btn-${u.id}`}
                                    className="px-3 py-1.5 rounded-full bg-white/5 hover:bg-orange-500/15 border border-white/10 hover:border-orange-500/30 text-xs inline-flex items-center gap-1.5 text-zinc-300 hover:text-orange-300 transition"
                                >
                                    <Sparkles className="w-3 h-3" /> Grant
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>

            <GrantDialog user={granting} onClose={() => setGranting(null)} onApplied={load} />
        </div>
    );
}

function InviteCodesPanel() {
    const [codes, setCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [plan, setPlan] = useState("pro");
    const [credits, setCredits] = useState(500);
    const [maxUses, setMaxUses] = useState(1);
    const [label, setLabel] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/invite-codes");
            setCodes(data.codes || []);
        } catch (_e) {
            toast.error("Failed to load codes");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const create = async () => {
        setCreating(true);
        try {
            await api.post("/admin/invite-codes", { plan, credits: Number(credits), max_uses: Number(maxUses), label });
            toast.success("Invite code created");
            setLabel("");
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Create failed");
        } finally {
            setCreating(false);
        }
    };

    const revoke = async (code) => {
        if (!window.confirm(`Revoke ${code}? Existing users keep their access; the code stops working for new signups.`)) return;
        try {
            await api.delete(`/admin/invite-codes/${code}`);
            toast.success("Code revoked");
            await load();
        } catch (_e) {
            toast.error("Revoke failed");
        }
    };

    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const copy = (code) => {
        const link = `${origin}/signup?code=${code}`;
        navigator.clipboard.writeText(link);
        toast.success("Signup link copied");
    };

    return (
        <div className="grid lg:grid-cols-12 gap-6">
            <div className="lg:col-span-5">
                <div className="glass-card rounded-2xl p-6">
                    <h3 className="font-heading text-lg font-semibold mb-1">Create invite code</h3>
                    <p className="text-xs text-zinc-500 mb-5">Share the signup link with employees — they get the plan + credits automatically.</p>
                    <div className="space-y-4">
                        <div>
                            <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Plan</label>
                            <div className="mt-2 grid grid-cols-1 gap-2">
                                {PLAN_OPTIONS.map((p) => (
                                    <button
                                        key={p.id}
                                        type="button"
                                        onClick={() => setPlan(p.id)}
                                        data-testid={`code-plan-${p.id}`}
                                        className={`text-left px-3 py-2 rounded-lg border text-sm transition ${plan === p.id ? "border-orange-500/40 bg-orange-500/8 text-white" : "border-white/10 bg-white/3 text-zinc-400 hover:bg-white/8"}`}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Credits</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={credits}
                                    onChange={(e) => setCredits(e.target.value)}
                                    data-testid="code-credits-input"
                                    className="mt-2 w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white font-mono focus:outline-none focus:ring-2 focus:ring-orange-500/60"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Max uses</label>
                                <input
                                    type="number"
                                    min="1"
                                    value={maxUses}
                                    onChange={(e) => setMaxUses(e.target.value)}
                                    data-testid="code-maxuses-input"
                                    className="mt-2 w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white font-mono focus:outline-none focus:ring-2 focus:ring-orange-500/60"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Label (optional)</label>
                            <input
                                value={label}
                                onChange={(e) => setLabel(e.target.value)}
                                placeholder="e.g. Agency team Q1 2026"
                                data-testid="code-label-input"
                                className="mt-2 w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-orange-500/60"
                            />
                        </div>
                        <button
                            type="button"
                            onClick={create}
                            disabled={creating}
                            data-testid="code-create-btn"
                            className="btn-magic w-full py-3 rounded-xl text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                            {creating ? "Creating..." : "Generate code"}
                        </button>
                    </div>
                </div>
            </div>

            <div className="lg:col-span-7">
                <div className="glass-card rounded-2xl overflow-hidden">
                    <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
                        <h3 className="font-heading text-base font-semibold">Active codes</h3>
                        <span className="text-xs text-zinc-500">{codes.length} total</span>
                    </div>
                    {loading ? (
                        <div className="p-10 text-center text-zinc-500 text-sm">Loading...</div>
                    ) : codes.length === 0 ? (
                        <div className="p-10 text-center text-zinc-500 text-sm" data-testid="admin-codes-empty">No codes yet. Create your first one →</div>
                    ) : (
                        codes.map((c) => (
                            <div key={c.code} className={`px-5 py-4 border-b border-white/5 last:border-b-0 ${c.revoked ? "opacity-50" : ""}`} data-testid={`code-row-${c.code}`}>
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="min-w-0">
                                        <div className="font-mono text-lg font-bold tracking-wider text-orange-300">{c.code}</div>
                                        {c.label && <div className="text-xs text-zinc-500 mt-0.5">{c.label}</div>}
                                        <div className="text-xs text-zinc-500 mt-1.5">
                                            <span className="capitalize">{c.plan}</span> · {c.credits} credits · {c.uses}/{c.max_uses} used
                                            {c.revoked && <span className="ml-2 text-rose-400">· revoked</span>}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {!c.revoked && (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={() => copy(c.code)}
                                                    data-testid={`code-copy-${c.code}`}
                                                    className="px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-xs inline-flex items-center gap-1.5"
                                                >
                                                    <Copy className="w-3 h-3" /> Copy link
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => revoke(c.code)}
                                                    data-testid={`code-revoke-${c.code}`}
                                                    className="px-3 py-1.5 rounded-full bg-white/5 hover:bg-rose-500/15 border border-white/10 hover:border-rose-500/30 text-xs text-zinc-400 hover:text-rose-400"
                                                >
                                                    <Trash2 className="w-3 h-3" />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}

export default function Admin() {
    const { user } = useAuth();
    const [tab, setTab] = useState("users");
    const [stats, setStats] = useState(null);

    useEffect(() => {
        if (user?.role === "admin") {
            api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
        }
    }, [user]);

    if (user && user.role !== "admin") {
        return <Navigate to="/dashboard" replace />;
    }

    return (
        <AppShell subtitle="Admin" title="Team & access control.">
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
                    <StatTile label="Total users" value={stats.total_users} icon={Users} />
                    <StatTile label="Trial" value={stats.trial_users} icon={Sparkles} />
                    <StatTile label="Paid" value={stats.paid_users} icon={ShieldCheck} />
                    <StatTile label="Enhancements" value={stats.total_enhancements} icon={Sparkles} />
                    <StatTile label="Invite codes" value={stats.active_invite_codes} icon={Ticket} />
                </div>
            )}
            <div className="flex gap-2 mb-7">
                <button
                    type="button"
                    onClick={() => setTab("users")}
                    data-testid="admin-tab-users"
                    className={`px-4 py-2 rounded-full text-sm border transition inline-flex items-center gap-2 ${tab === "users" ? "bg-orange-500/15 border-orange-500/40 text-orange-300" : "bg-white/3 border-white/10 text-zinc-400 hover:bg-white/8"}`}
                >
                    <Users className="w-4 h-4" /> Users
                </button>
                <button
                    type="button"
                    onClick={() => setTab("codes")}
                    data-testid="admin-tab-codes"
                    className={`px-4 py-2 rounded-full text-sm border transition inline-flex items-center gap-2 ${tab === "codes" ? "bg-orange-500/15 border-orange-500/40 text-orange-300" : "bg-white/3 border-white/10 text-zinc-400 hover:bg-white/8"}`}
                >
                    <Ticket className="w-4 h-4" /> Invite codes
                </button>
            </div>

            {tab === "users" ? <UsersPanel /> : <InviteCodesPanel />}
        </AppShell>
    );
}
